import { useCallback, useEffect, useState } from 'react'
import { X } from '@digital-twins/design-system/icons'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import Link from '@/routing/Link'
import { cn } from '@/lib/utils'
import { publicApi } from '@/lib/api'

const PORTRAIT_COUNT = 5
const PORTRAIT_CACHE_KEY = 'guest-guard:portrait-urls'

const FALLBACK_PORTRAITS = [
  'https://storage.googleapis.com/fairgen-twins-profiles/persona-images/meticulous-elder-african-leader.png',
  'https://storage.googleapis.com/fairgen-twins-profiles/persona-images/data-analyst-female.png',
  'https://storage.googleapis.com/fairgen-twins-profiles/persona-images/warm-smile-white-investor.png',
  'https://storage.googleapis.com/fairgen-twins-profiles/persona-images/patient-western-european-coordinator.png',
  'https://storage.googleapis.com/fairgen-twins-profiles/persona-images/dynamic-black-innovator.png',
]

function readCached(): Array<string> | null {
  try {
    const raw = sessionStorage.getItem(PORTRAIT_CACHE_KEY)
    if (!raw) return null
    const urls = JSON.parse(raw) as Array<string>
    return Array.isArray(urls) && urls.length >= PORTRAIT_COUNT ? urls : null
  } catch {
    return null
  }
}

function writeCache(urls: Array<string>) {
  try {
    sessionStorage.setItem(PORTRAIT_CACHE_KEY, JSON.stringify(urls))
  } catch { /* best-effort */ }
}

function usePortraitUrls() {
  const [urls, setUrls] = useState<Array<string>>(() => readCached() ?? FALLBACK_PORTRAITS)

  const applyUrls = useCallback((fetched: Array<string>) => {
    setUrls(fetched.slice(0, PORTRAIT_COUNT))
    writeCache(fetched)
  }, [])

  useEffect(() => {
    if (readCached()) return
    let cancelled = false
    publicApi
      .getRandomPortraitUrls(PORTRAIT_COUNT)
      .then((fetched) => {
        if (!cancelled && fetched.length >= PORTRAIT_COUNT) applyUrls(fetched)
      })
      .catch(() => { /* keep fallbacks */ })
    return () => { cancelled = true }
  }, [applyUrls])

  return urls
}

interface GuestNavGuardModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function GuestNavGuardModal({ open, onOpenChange }: GuestNavGuardModalProps) {
  const portraits = usePortraitUrls()

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="w-[540px] gap-0 rounded-[16px] border-border dark:border-[hsl(var(--card-inset))] bg-background dark:bg-card p-0 shadow-[0px_4px_19.6px_0px_rgba(0,0,0,0.08)]"
      >
        <div className="flex items-center justify-end p-3">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-muted-foreground hover:text-foreground dark:text-white/70 dark:hover:text-white transition-colors"
            aria-label="Close"
            data-testid="guest-guard-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col items-center gap-7 px-[60px] pb-10">
          {/* Twin portrait circles */}
          <div className="flex items-center justify-center -space-x-3">
            {portraits.map((url, i) => (
              <div
                key={url}
                className={cn(
                  'relative h-12 w-12 shrink-0 overflow-hidden rounded-full border-2 border-background dark:border-card bg-muted',
                  i === 2 ? 'z-30 h-14 w-14' : i === 1 || i === 3 ? 'z-20' : 'z-10',
                )}
              >
                <img
                  src={url}
                  alt=""
                  className="h-full w-full object-cover object-top"
                  loading="eager"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden' }}
                />
              </div>
            ))}
          </div>

          <AlertDialogHeader className="items-center gap-3 space-y-0">
            <AlertDialogTitle className="font-display text-2xl font-normal leading-[1.2] tracking-[-0.48px] text-foreground dark:text-white text-center">
              Want to explore more?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base leading-[1.6] text-muted-foreground text-center">
              Create your own audiences and studies, and access a rich marketplace
              of B2B and B2C audiences across categories globally.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="flex items-center justify-center gap-4">
            <AlertDialogAction asChild>
              <Link
                href="/register"
                data-testid="guest-guard-modal-signup"
                className="h-10 rounded-[6px] px-4 text-base font-medium bg-cta text-cta-foreground hover:bg-cta/90 inline-flex items-center"
              >
                Sign up
              </Link>
            </AlertDialogAction>
            <AlertDialogCancel
              onClick={() => onOpenChange(false)}
              data-testid="guest-guard-modal-dismiss"
              className="h-10 rounded-[6px] border-foreground dark:border-input-focus bg-transparent px-4 text-base font-medium text-foreground dark:text-input-focus hover:bg-transparent hover:text-foreground dark:hover:text-input-focus"
            >
              Never mind
            </AlertDialogCancel>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
